import { useQuery } from '@apollo/client';
import { Spinner } from '@chakra-ui/react';
import { assert } from '@common/errors';
import {
    getName,
    PlentyGoalAccountFinancialEntity,
} from '@common/financialEntities';
import { Goal } from '@common/goals';
import {
    CurrentRateInfo,
    FinancialEntityDocument,
} from '@common/graphql/generated';
import { useCurrentRateInfo } from '@common/react/rates';
import { createContext, useContext } from 'react';
import { EntityViewHeader } from '../common/EntityView';
import { FlexCol } from '../common/Flex';
import { configByGoalType } from './config';
import GoalAccountDetailView from './GoalAccountDetailView';
import { useGoal } from './helpers';

type GoalAccountContext = {
    goal: Goal;
    goalAccount: PlentyGoalAccountFinancialEntity;
    goalAccountName: string;
    annualReturn: number;
    closeDrawer: () => void;
};

export const GoalAccountContext = createContext<GoalAccountContext | null>(
    null
);

export const useGoalAccountContext = (): GoalAccountContext => {
    const ctx = useContext(GoalAccountContext);
    assert(ctx, 'Value must be passed to GoalAccountContext');
    return ctx;
};

export interface GoalAccountDetailContainerProps {
    id: string;
    onClose: VoidFunction;
}

export default function GoalAccountDetailContainer({
    id,
    onClose,
}: GoalAccountDetailContainerProps) {
    const { currentRateInfo } = useCurrentRateInfo();
    const { data: goalAccountData } = useQuery(FinancialEntityDocument, {
        variables: { id },
    });
    const goalAccount = goalAccountData?.financialEntity as
        | PlentyGoalAccountFinancialEntity
        | null
        | undefined;
    const { goal } = useGoal(goalAccount?.goalId);

    if (!goal || !goalAccount || !currentRateInfo) {
        return (
            <FlexCol w="full" h="full">
                <EntityViewHeader>
                    <Spinner color="blue.500" />
                </EntityViewHeader>
            </FlexCol>
        );
    }

    return (
        <GoalAccountDetailContainerInner
            goal={goal}
            goalAccount={goalAccount}
            currentRateInfo={currentRateInfo}
            onClose={onClose}
        />
    );
}

const GoalAccountDetailContainerInner = ({
    goal,
    goalAccount,
    currentRateInfo,
    onClose,
}: {
    goal: Goal;
    goalAccount: PlentyGoalAccountFinancialEntity;
    currentRateInfo: CurrentRateInfo;
    onClose: VoidFunction;
}) => {
    const config = configByGoalType[goal.type];
    const annualReturn = config.getAnnualReturn({
        investmentType: goalAccount.info.investmentType,
        riskScore: goalAccount.info.riskScore,
        currentRateInfo,
    });

    const goalAccountName = getName(goalAccount, { withTypeAsName: true });

    return (
        <GoalAccountContext.Provider
            value={{
                goal,
                goalAccount,
                goalAccountName,
                closeDrawer: onClose,
                annualReturn,
            }}
        >
            <GoalAccountDetailView />
        </GoalAccountContext.Provider>
    );
};
